/**
 * Unified KPI Analytics Page
 *
 * Phase 24.4.1: Replaces the 3 separate KPI analytics pages (Response Time,
 * Activity Count, Compliance) with a single page that supports any KPI type.
 *
 * Features:
 * - KPI selector dropdown (populated from org's KPI library)
 * - Summary stat cards (latest value, average, min/max, threshold status counts)
 * - Time series line chart of primaryValue over date range
 * - Group-by bar chart (station/operator/day/week)
 * - Results table with status chips and 25-row pages
 * - Date range picker (7d, 30d, 90d)
 * - Values formatted by unit (seconds as mm:ss, percentages with %, counts as integers)
 *
 * Phase 41A: the URL holds the page state (?code, ?status, ?page, ?period,
 * ?view), and every query reads one as-of snapshot window that Refresh renews.
 * The as-of label and the results table read times in the org's time zone
 * (`orgTimezone` from GET /api/me). A results row with a structured reason
 * opens its Why panel inline, and "View details" passes this page's URL to the
 * trigger page for its back link.
 *
 * Feature 2 (G-34): `?shift=` holds one crew's shift id. It filters every query
 * but the By Shift grouping, which stays the crew comparison; a counts strip
 * under that chart opens a crew's flagged results.
 */

import { Fragment, useState, useMemo, useCallback, useEffect, useRef, type RefObject } from 'react';
import { useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Gauge,
  TrendingUp,
  Building2,
  Users,
  BarChart3,
  Calendar,
  AlertTriangle,
  ArrowRight,
  Loader2,
  Activity,
  Target,
  ExternalLink,
  Clock,
  Layers,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import {
  useKpiLookup,
  useKpiAnalytics,
  useKpiMeasurements,
  type KpiMeasurementListItem,
  type KpiMeasurementsResponse,
} from '../../hooks/useKpi';
import { useCurrentUser } from '../../hooks/useUsers';
import { useShiftLookup } from '../../hooks/useShifts';
import type {
  KpiAnalyticsResponse,
  KpiAnalyticsSummary,
  KpiAggregation,
  KpiResultStatusToken,
} from '@guild/shared';
import { ApiError } from '../../lib/api';
import {
  DATE_TIME_OPTIONS,
  describeThresholds,
  formatDayKey,
  formatFootageWindow,
  formatInTimeZone,
  formatKpiValue,
  hasWhyPanel,
  noValueLabel,
  rowObservationStatus,
  whyHeadline,
} from '../../lib/kpi-format';
import { cn } from '../../lib/utils';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { KpiWhyPanel } from '../../components/kpi/KpiWhyPanel';
import { CumulativeKpiView } from './components/CumulativeKpiView';
import { ShiftCountsStrip } from './components/ShiftCountsStrip';

// ============================================
// CONSTANTS
// ============================================

type Period = '7d' | '30d' | '90d';
// Phase 30 Wave 5: 'shift' is now wired to real data (was Phase-9.1 day-fallback).
// 'operator-shift' is a cross-cut composite for per-operator-per-shift breakdown.
type GroupBy =
  | 'station'
  | 'operator'
  | 'shift'
  | 'operator-shift'
  | 'day'
  | 'week';

type ViewMode = 'per-trigger' | 'cumulative';

const PERIOD_OPTIONS: { value: Period; label: string; days: number }[] = [
  { value: '7d', label: '7 Days', days: 7 },
  { value: '30d', label: '30 Days', days: 30 },
  { value: '90d', label: '90 Days', days: 90 },
];

const GROUP_BY_OPTIONS: { value: GroupBy; label: string; icon: LucideIcon }[] = [
  { value: 'station', label: 'Zone', icon: Building2 },
  { value: 'operator', label: 'Operator', icon: Users },
  { value: 'shift', label: 'Shift', icon: Clock },
  { value: 'operator-shift', label: 'Operator / Shift', icon: Layers },
  { value: 'day', label: 'Day', icon: Calendar },
  { value: 'week', label: 'Week', icon: Calendar },
];

// G-19: offered only when the summary shows a measured result with an operator.
const OPERATOR_GROUPINGS: ReadonlySet<GroupBy> = new Set<GroupBy>(['operator', 'operator-shift']);

const STATUS_COLORS = {
  normal: '#10b981',
  warning: '#f59e0b',
  critical: '#ef4444',
};

// Phase 41A (S6): rows per results-table page.
const PAGE_SIZE = 25;

// Phase 41A: no row's Why panel is open (a new page starts this way).
const NO_EXPANDED_ROWS: ReadonlySet<string> = new Set();

// Phase 41A (S7): the URL's `status` holds a chip id; the list API takes the
// chip's status tokens. No `status` in the URL means All.
type StatusChipId = 'flagged' | 'critical' | 'warning' | 'normal' | 'not_observed';

const STATUS_CHIPS: ReadonlyArray<{
  id: StatusChipId;
  label: string;
  /** Fills the empty state: "No {emptyName} results in this period." */
  emptyName: string;
  tokens: readonly KpiResultStatusToken[];
}> = [
  { id: 'flagged', label: 'Flagged', emptyName: 'flagged', tokens: ['warning', 'critical'] },
  { id: 'critical', label: 'Critical', emptyName: 'critical', tokens: ['critical'] },
  { id: 'warning', label: 'Warning', emptyName: 'warning', tokens: ['warning'] },
  { id: 'normal', label: 'Normal', emptyName: 'normal', tokens: ['normal'] },
  { id: 'not_observed', label: 'Not observed', emptyName: 'not-observed', tokens: ['not_observed'] },
];

type StatusChipCounts = Record<StatusChipId | 'all', number>;

// ============================================
// URL STATE + CHIP COUNTS (Phase 41A)
// ============================================

/** `?period=`: anything other than 7d / 30d / 90d reads as 30d. */
function parsePeriod(raw: string | null): Period {
  return PERIOD_OPTIONS.find((option) => option.value === raw)?.value ?? '30d';
}

/** `?status=`: an unknown chip id reads as All (null). */
function parseStatusChip(raw: string | null): StatusChipId | null {
  return STATUS_CHIPS.find((chip) => chip.id === raw)?.id ?? null;
}

/**
 * `?page=`: anything but a positive whole number reads as 1. A page past the
 * end is clamped to the last page once the list total is known.
 */
function parsePage(raw: string | null): number {
  const page = raw !== null && /^[0-9]+$/.test(raw) ? Number(raw) : NaN;
  return Number.isSafeInteger(page) && page >= 1 ? page : 1;
}

/**
 * Feature 2 (G-34) `?shift=`: a shift id (one crew's results). Anything but a
 * uuid reads as no filter (null), so the API's uuid check never returns a 400.
 */
function parseShiftParam(raw: string | null): string | null {
  return raw !== null && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)
    ? raw
    : null;
}

/**
 * Chip counts from the analytics summary. Every field is read through a
 * default (S16): the previous API sends no `notObservedCount`.
 */
function statusChipCounts(summary: KpiAnalyticsSummary | undefined): StatusChipCounts {
  const measured = summary?.totalMeasurements ?? 0;
  const notObserved = summary?.notObservedCount ?? 0;
  const warning = summary?.warningCount ?? 0;
  const critical = summary?.criticalCount ?? 0;
  return {
    all: measured + notObserved,
    flagged: warning + critical,
    critical,
    warning,
    normal: summary?.normalCount ?? 0,
    not_observed: notObserved,
  };
}

function countResults(count: number): string {
  return `${count} ${count === 1 ? 'result' : 'results'}`;
}

/** Brings the results heading into view and focuses it ("N flagged" click). */
function focusResultsHeading(heading: HTMLElement) {
  heading.focus({ preventScroll: true });
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
}

// ============================================
// VALUE FORMATTING
// ============================================

function getThresholdStatusColor(status: string | null | undefined): string {
  switch (status) {
    case 'normal':
      return 'text-emerald-600';
    case 'warning':
      return 'text-amber-600';
    case 'critical':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
}

function getThresholdStatusBgColor(status: string | null | undefined): string {
  switch (status) {
    case 'normal':
      return 'bg-emerald-100';
    case 'warning':
      return 'bg-amber-100';
    case 'critical':
      return 'bg-red-100';
    default:
      return 'bg-gray-100';
  }
}

function getThresholdDotColor(status: string | null | undefined): string {
  switch (status) {
    case 'normal':
      return 'bg-emerald-500';
    case 'warning':
      return 'bg-amber-500';
    case 'critical':
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
}

// ============================================
// MAIN COMPONENT
// ============================================

export function KpiAnalyticsPage() {
  useDocumentTitle('KPI Analytics');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Phase 41A: the URL is the state (deep links, reloads). Defaults: no KPI,
  // All, page 1, 30 days, per-trigger view; invalid values read as defaults.
  // Phase 30 Wave 5: `?view=cumulative` deep link arrives from the per-row
  // neutral pill in MeasurementsTable + the TriggerDetailPage cumulative pill.
  const code = searchParams.get('code') ?? '';
  const period = parsePeriod(searchParams.get('period'));
  const statusChip = parseStatusChip(searchParams.get('status'));
  const page = parsePage(searchParams.get('page'));
  const viewMode: ViewMode =
    searchParams.get('view') === 'cumulative' ? 'cumulative' : 'per-trigger';
  // Feature 2 (G-34): one crew's results, or null for every crew
  const shiftFilter = parseShiftParam(searchParams.get('shift'));

  const [groupBy, setGroupBy] = useState<GroupBy>('station');

  // Merge updates into the current params (copied from AnalyticsPage.tsx):
  // null or '' deletes a param; `replace` keeps filter changes out of history.
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Phase 41A (G-42): one as-of snapshot. Every query reads the window
  // [asOf − period, asOf], so the cards, chips, charts, table and cumulative
  // view count the same results, and paging cannot skip or repeat a row. A new
  // KPI or period takes a new snapshot, whatever changed the URL; the state is
  // adjusted during render, so no query runs with the old snapshot. Refresh
  // takes a new one too.
  const snapshotKey = JSON.stringify([code, period]);
  const [snapshot, setSnapshot] = useState(() => ({ key: snapshotKey, asOf: new Date() }));
  if (snapshot.key !== snapshotKey) {
    setSnapshot({ key: snapshotKey, asOf: new Date() });
  }
  const { asOf } = snapshot;

  // Phase 41A (D5): the as-of label and the results table read in the org's
  // zone. While /api/me loads, or against an API without `orgTimezone`, the
  // kpi-format helpers use the browser's zone, and their zone labels name it.
  const { data: currentUser } = useCurrentUser();
  const orgTimezone = currentUser?.orgTimezone;

  // Fetch KPI lookup for dropdown
  const { data: kpiLookup, isLoading: lookupLoading } = useKpiLookup();

  // Find selected KPI details
  const selectedKpi = useMemo(
    () => kpiLookup?.find((k) => k.code === code) ?? null,
    [kpiLookup, code]
  );

  // Compute date range: the snapshot window
  const dateRange = useMemo(() => {
    const periodConfig = PERIOD_OPTIONS.find((p) => p.value === period)!;
    const start = new Date(asOf.getTime() - periodConfig.days * 24 * 60 * 60 * 1000);
    return {
      startDate: start.toISOString(),
      endDate: asOf.toISOString(),
    };
  }, [asOf, period]);

  // Time series data (grouped by day or week); its summary feeds the cards and chips
  const timeGroupBy = period === '90d' ? 'week' : 'day';
  const {
    data: trendData,
    isLoading: trendLoading,
    error: trendError,
  } = useKpiAnalytics({
    kpiCode: code,
    groupBy: timeGroupBy,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    shiftId: shiftFilter ?? undefined,
  });

  // Phase 41A (S17): the analytics call validates `?code` (system and org KPIs,
  // inactive ones included); a 400 means no such KPI. The chips and table wait
  // until it has resolved the KPI: the list API ignores an unknown `kpiCode`, so
  // rendering earlier could show other KPIs' rows.
  const kpiUnavailable =
    code !== '' && trendError instanceof ApiError && trendError.status === 400;
  const kpiResolved = trendData !== undefined && !kpiUnavailable;
  const summary = trendData?.summary;

  // G-19: the Operator groupings appear only once the summary shows a measured
  // result with an operator, so they stay hidden while it loads (fail closed).
  // A hidden current grouping falls back to Zone.
  const operatorGroupingsAllowed = (summary?.attributedCount ?? 0) > 0;
  const groupByOptions = operatorGroupingsAllowed
    ? GROUP_BY_OPTIONS
    : GROUP_BY_OPTIONS.filter((option) => !OPERATOR_GROUPINGS.has(option.value));
  const effectiveGroupBy: GroupBy =
    !operatorGroupingsAllowed && OPERATOR_GROUPINGS.has(groupBy) ? 'station' : groupBy;

  // Group-by data (station, operator, etc.). Feature 2: By Shift is the crew
  // comparison (and feeds the counts strip), so it ignores the crew filter;
  // every other grouping honours it.
  const { data: groupedData, isLoading: groupedLoading } = useKpiAnalytics({
    kpiCode: code,
    groupBy: effectiveGroupBy,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    shiftId: effectiveGroupBy !== 'shift' ? (shiftFilter ?? undefined) : undefined,
  });

  // Feature 2: crew names for the filter pill and the counts strip (active
  // shifts, in their configured order)
  const { data: shiftLookup } = useShiftLookup();
  const shiftFilterName = shiftLookup?.find((shift) => shift.id === shiftFilter)?.name;

  // Results: one page of the window, filtered by the status chip
  const statusTokens = STATUS_CHIPS.find((chip) => chip.id === statusChip)?.tokens.join(',');
  const {
    data: measurementsData,
    isError: measurementsError,
    isPlaceholderData: measurementsStale,
  } = useKpiMeasurements(
    {
      kpiCode: code,
      status: statusTokens,
      shiftId: shiftFilter ?? undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    { enabled: kpiResolved }
  );

  // `?page` past the end clamps to the last page once the total is known
  const resultsTotal = measurementsData?.pagination.total;
  const lastPage =
    resultsTotal === undefined ? null : Math.max(1, Math.ceil(resultsTotal / PAGE_SIZE));
  useEffect(() => {
    if (lastPage !== null && page > lastPage) {
      updateParams({ page: String(lastPage) });
    }
  }, [page, lastPage, updateParams]);

  // Changing the KPI keeps the status, period and view, and resets the page
  const handleKpiChange = (nextCode: string) => {
    updateParams({ code: nextCode || null, page: null });
  };

  const handlePeriodChange = (nextPeriod: Period) => {
    if (nextPeriod === period) return;
    updateParams({ period: nextPeriod, page: null });
  };

  const handleViewModeChange = (mode: ViewMode) => {
    updateParams({ view: mode === 'cumulative' ? 'cumulative' : null });
  };

  // Chips filter the table only, never the cards or charts
  const handleStatusChange = (chip: StatusChipId | null) => {
    if (chip === statusChip) return;
    updateParams({ status: chip, page: null });
  };

  const handlePageChange = (nextPage: number) => {
    updateParams({ page: String(nextPage) });
  };

  const handleRefresh = () => {
    setSnapshot((current) => ({ ...current, asOf: new Date() }));
    updateParams({ page: null });
  };

  // "N flagged" opens the flagged results in the per-trigger view (the only
  // view with a table), then scrolls to the table and focuses its heading. From
  // the Cumulative view the table mounts in a later commit (React Router renders
  // URL changes in a transition), so the focus waits for it.
  // Feature 2: a crew's "Show N flagged" in the counts strip passes its shift
  // id, which also sets the crew filter.
  const resultsHeadingRef = useRef<HTMLHeadingElement>(null);
  const focusResultsPending = useRef(false);
  const showFlagged = (shiftId?: string) => {
    updateParams({
      status: 'flagged',
      page: '1',
      view: null,
      ...(shiftId ? { shift: shiftId } : {}),
    });
    // A new crew re-keys the trend call, so the table unmounts until it
    // resolves (kpiResolved); the effect below focuses its heading then.
    if (resultsHeadingRef.current && (!shiftId || shiftId === shiftFilter)) {
      focusResultsHeading(resultsHeadingRef.current);
    } else {
      focusResultsPending.current = true;
    }
  };
  // The cards' action takes no argument, so a click event never arrives as an id
  const handleShowFlagged = () => showFlagged();

  const handleClearShift = () => {
    updateParams({ shift: null, page: null });
  };
  useEffect(() => {
    if (focusResultsPending.current && resultsHeadingRef.current) {
      focusResultsPending.current = false;
      focusResultsHeading(resultsHeadingRef.current);
    }
  }, [viewMode, kpiResolved, shiftFilter]);

  // The per-row "Part of shift window" pill opens the Cumulative view with the
  // other params merged in, so the period, status and page survive the switch.
  const cumulativeHref = useMemo(() => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'cumulative');
    return `/analytics/kpi?${next.toString()}`;
  }, [searchParams]);

  // S17: a resolved code missing from the lookup (which lists active org-owned
  // KPIs only) gets its own dropdown option, labelled when the KPI is inactive.
  const kpiIsActive = trendData?.kpi?.isActive ?? true;
  const offListKpiLabel =
    kpiResolved && trendData && !kpiLookup?.some((kpi) => kpi.code === code)
      ? `${trendData.kpi.name}${kpiIsActive ? '' : ' (inactive)'}`
      : null;

  // In the org's zone, like the results table's times; the label names the zone
  const asOfLabel = formatInTimeZone(asOf, orgTimezone, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  // Get KPI unit/format from analytics response or lookup
  const kpiUnit = trendData?.kpi?.unit ?? selectedKpi?.unit ?? null;
  const kpiDisplayFormat = trendData?.kpi?.displayFormat ?? null;
  const kpiThresholds = trendData?.kpi?.thresholds;
  // Phase 30 Wave 5 §7.1: per-row badge suppression for sum-aggregated KPIs.
  // KpiAnalyticsResponse already carries `aggregationType` (kpi.aggregationType).
  const kpiAggregationType: KpiAggregation | null = trendData?.kpi?.aggregationType ?? null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-guild-green/10 text-guild-green">
            <Gauge size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">KPI Analytics</h1>
            <p className="text-sm text-gray-500">
              Analyze key performance indicators across your organization
            </p>
          </div>
        </div>
      </div>

      {/* KPI Selector + Date Range */}
      <div className="flex flex-wrap items-center gap-4">
        {/* KPI Dropdown */}
        <div className="min-w-[280px]">
          <select
            value={code}
            onChange={(e) => handleKpiChange(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-medium bg-white shadow-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green"
          >
            <option value="">Select a KPI...</option>
            {offListKpiLabel && <option value={code}>{offListKpiLabel}</option>}
            {lookupLoading ? (
              <option disabled>Loading KPIs...</option>
            ) : (
              kpiLookup?.map((kpi) => (
                <option key={kpi.id} value={kpi.code}>
                  {kpi.name}
                  {kpi.category ? ` (${kpi.category})` : ''}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Period Toggle */}
        {code && !kpiUnavailable && (
          <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handlePeriodChange(option.value)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200',
                  period === option.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {/* Feature 2 (G-34): the crew filter; × shows every crew again */}
        {code && !kpiUnavailable && shiftFilter && (
          <span className="inline-flex items-center gap-1 rounded-full border border-guild-green/30 bg-guild-mint/40 py-1 pl-3 pr-1 text-xs font-medium text-guild-green">
            {shiftFilterName ? `Crew: ${shiftFilterName}` : 'Selected shift'}
            <button
              type="button"
              onClick={handleClearShift}
              aria-label="Clear the crew filter"
              className="rounded-full p-0.5 transition-colors hover:bg-guild-green/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-guild-green"
            >
              <X size={12} />
            </button>
          </span>
        )}

        {/* Phase 41A: the snapshot every query reads, and Refresh */}
        {code && !kpiUnavailable && (
          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-gray-500">
              As of <time dateTime={asOf.toISOString()}>{asOfLabel}</time>
            </span>
            <button
              type="button"
              onClick={handleRefresh}
              aria-label="Refresh results"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* Empty State - No KPI Selected */}
      {!code && (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-20">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm">
            <Gauge size={32} className="text-gray-400" />
          </div>
          <p className="mt-4 text-sm font-medium text-gray-900">Select a KPI to view analytics</p>
          <p className="mt-1 text-sm text-gray-500">
            Choose from your organization's KPI library above
          </p>
          {kpiLookup && kpiLookup.length === 0 && (
            <Link
              to="/kpis"
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-guild-green px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-guild-green/90"
            >
              Create your first KPI
              <ArrowRight size={14} />
            </Link>
          )}
        </div>
      )}

      {/* Phase 41A (S17): the analytics call does not resolve this code */}
      {code && kpiUnavailable && (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-20">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm">
            <AlertTriangle size={32} className="text-gray-400" />
          </div>
          <p className="mt-4 text-sm font-medium text-gray-900">This KPI isn't available</p>
          <p className="mt-1 text-sm text-gray-500">Choose a KPI from the list above</p>
        </div>
      )}

      {/* Analytics Content - Shown when KPI is selected */}
      {code && !kpiUnavailable && (
        <>
          {/* Phase 30 Wave 5: top-level view toggle */}
          <ViewModeToggle viewMode={viewMode} onChange={handleViewModeChange} />

          {/* Summary Cards (shared between per-trigger and cumulative views) */}
          <SummaryCards
            data={trendData}
            isLoading={trendLoading}
            unit={kpiUnit}
            displayFormat={kpiDisplayFormat}
            onShowFlagged={handleShowFlagged}
          />

          {viewMode === 'per-trigger' ? (
            <>
              {/* Charts Row */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Trend Chart */}
                <TrendChart
                  data={trendData}
                  isLoading={trendLoading}
                  unit={kpiUnit}
                  displayFormat={kpiDisplayFormat}
                  thresholds={kpiThresholds}
                />

                {/* Group-By Bar Chart */}
                <GroupByChart
                  data={groupedData}
                  isLoading={groupedLoading}
                  groupBy={effectiveGroupBy}
                  options={groupByOptions}
                  onGroupByChange={setGroupBy}
                  unit={kpiUnit}
                  displayFormat={kpiDisplayFormat}
                />
              </div>

              {/* Feature 2 (G-34): each crew's measured and flagged counts, under By Shift */}
              {effectiveGroupBy === 'shift' && (
                <ShiftCountsStrip
                  groups={groupedData?.groups}
                  shifts={shiftLookup}
                  activeShiftId={shiftFilter}
                  onShowFlagged={showFlagged}
                />
              )}

              {/* Results Table — rendered only once the analytics call resolved the KPI (S17) */}
              {kpiResolved && (
                <MeasurementsTable
                  data={measurementsData}
                  isError={measurementsError}
                  isStale={measurementsStale}
                  page={page}
                  lastPage={lastPage}
                  statusChip={statusChip}
                  chipCounts={statusChipCounts(summary)}
                  onStatusChange={handleStatusChange}
                  onPageChange={handlePageChange}
                  headingRef={resultsHeadingRef}
                  unit={kpiUnit}
                  displayFormat={kpiDisplayFormat}
                  timeZone={orgTimezone}
                  aggregationType={kpiAggregationType}
                  cumulativeHref={cumulativeHref}
                  navigate={navigate}
                  detailsFrom={location.pathname + location.search}
                />
              )}
            </>
          ) : (
            <CumulativeKpiView
              kpiCode={code}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              shiftId={shiftFilter ?? undefined}
            />
          )}
        </>
      )}
    </div>
  );
}

// ============================================
// VIEW MODE TOGGLE (Phase 30 Wave 5)
// ============================================

function ViewModeToggle({
  viewMode,
  onChange,
}: {
  viewMode: ViewMode;
  onChange: (mode: ViewMode) => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1 w-fit">
      {([
        { value: 'per-trigger', label: 'Per-Trigger' },
        { value: 'cumulative', label: 'Cumulative (per shift window)' },
      ] as Array<{ value: ViewMode; label: string }>).map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200',
            viewMode === option.value
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// ============================================
// SUMMARY CARDS
// ============================================

function SummaryCards({
  data,
  isLoading,
  unit,
  displayFormat,
  onShowFlagged,
}: {
  data: KpiAnalyticsResponse | undefined;
  isLoading: boolean;
  unit: string | null;
  displayFormat: string | null;
  /** Phase 41A: the "N flagged" card's action, offered when N > 0. */
  onShowFlagged: () => void;
}) {
  const summary = data?.summary;
  const flaggedCount = summary ? summary.criticalCount + summary.warningCount : 0;
  // Phase 41A: M = measured (the legacy `totalMeasurements`), K = saved with no
  // value; the previous API sends no K (S16). With M = 0 the API's NULL
  // average, median and range arrive as 0, so they read "--" instead (S11).
  const measured = summary?.totalMeasurements ?? 0;
  const notObserved = summary?.notObservedCount ?? 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <SummaryCard
        title="Average"
        value={measured > 0 ? formatKpiValue(summary?.avgValue, unit, displayFormat) : '--'}
        subtitle={`${measured} measured · ${notObserved} not observed`}
        icon={Activity}
        iconBg="bg-blue-100"
        iconColor="text-blue-700"
        isLoading={isLoading}
      />
      <SummaryCard
        title="Median"
        value={measured > 0 ? formatKpiValue(summary?.medianValue, unit, displayFormat) : '--'}
        subtitle="50th percentile"
        icon={Target}
        iconBg="bg-guild-green/10"
        iconColor="text-guild-green"
        isLoading={isLoading}
      />
      <SummaryCard
        title="Range"
        value={
          summary && measured > 0
            ? `${formatKpiValue(summary.minValue, unit, displayFormat)} - ${formatKpiValue(summary.maxValue, unit, displayFormat)}`
            : '--'
        }
        subtitle="Min / Max"
        icon={TrendingUp}
        iconBg="bg-violet-100"
        iconColor="text-violet-700"
        isLoading={isLoading}
      />
      <SummaryCard
        title="Threshold Status"
        value={summary ? `${flaggedCount} flagged` : '--'}
        subtitle={
          summary
            ? `${summary.normalCount} normal, ${summary.warningCount} warning, ${summary.criticalCount} critical`
            : 'No data'
        }
        icon={AlertTriangle}
        iconBg={
          summary && (summary.criticalCount > 0)
            ? 'bg-red-100'
            : summary && (summary.warningCount > 0)
              ? 'bg-amber-100'
              : 'bg-gray-100'
        }
        iconColor={
          summary && (summary.criticalCount > 0)
            ? 'text-red-700'
            : summary && (summary.warningCount > 0)
              ? 'text-amber-700'
              : 'text-gray-500'
        }
        isLoading={isLoading}
        action={
          flaggedCount > 0
            ? {
                label: `Show ${flaggedCount} flagged ${flaggedCount === 1 ? 'result' : 'results'}`,
                onClick: onShowFlagged,
              }
            : undefined
        }
      />
      {/* Phase 41A (G-16): how the KPI's thresholds set each result's status */}
      {data && (
        <p className="col-span-full text-xs text-gray-500">
          {describeThresholds(data.kpi.thresholds, unit, displayFormat)}
        </p>
      )}
    </div>
  );
}

interface SummaryCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  isLoading?: boolean;
  /**
   * Phase 41A: makes the whole card one button with this accessible name. The
   * button overlays the card, so the card's own markup stays valid inside it.
   */
  action?: { label: string; onClick: () => void };
}

function SummaryCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconBg,
  iconColor,
  isLoading,
  action,
}: SummaryCardProps) {
  return (
    <div
      className={cn(
        'relative rounded-xl border border-gray-200 bg-white p-5',
        action && 'transition-colors hover:border-guild-green/40 hover:bg-gray-50'
      )}
    >
      <div className="flex items-center gap-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg', iconBg)}>
          <Icon size={20} className={iconColor} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          {isLoading ? (
            <div className="mt-1 h-7 w-20 animate-pulse rounded bg-gray-200" />
          ) : (
            <p className="text-2xl font-bold text-gray-900 tabular-nums truncate">{value}</p>
          )}
          <p className="text-xs text-gray-400 truncate">{subtitle}</p>
        </div>
        {action && <ArrowRight size={16} className="shrink-0 text-gray-400" />}
      </div>
      {action && (
        <button
          type="button"
          aria-label={action.label}
          onClick={action.onClick}
          className="absolute inset-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
        />
      )}
    </div>
  );
}

// ============================================
// TREND CHART
// ============================================

function TrendChart({
  data,
  isLoading,
  unit,
  displayFormat,
  thresholds,
}: {
  data: KpiAnalyticsResponse | undefined;
  isLoading: boolean;
  unit: string | null;
  displayFormat: string | null;
  thresholds?: { warning?: number; critical?: number; target?: number } | null;
}) {
  const chartData = useMemo(
    () =>
      data?.groups?.map((group) => ({
        date: group.key,
        value: group.value,
        count: group.count,
        warningCount: group.warningCount,
        criticalCount: group.criticalCount,
      })) ?? [],
    [data]
  );

  // Phase 41A: the keys are org-local dates ('YYYY-MM-DD'); formatDayKey reads
  // them without a time zone, so no browser zone shows them a day early.
  const keyStyle = data?.groupBy === 'week' ? 'week' : 'day';
  const formatDate = (key: string) => formatDayKey(key, keyStyle);

  const formatYAxis = (value: number) => {
    return formatKpiValue(value, unit, displayFormat);
  };

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ value: number | null }>;
    label?: string;
  }) => {
    if (!active || !payload?.length || !label) return null;

    const dataPoint = chartData.find((d) => d.date === label);
    const value = payload[0]?.value;

    return (
      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
        <p className="mb-2 text-xs font-medium text-gray-500">{formatDate(label)}</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-6">
            <span className="text-sm text-gray-600">Value</span>
            <span className="text-lg font-bold text-gray-900 tabular-nums">
              {formatKpiValue(value, unit, displayFormat)}
            </span>
          </div>
          {dataPoint && (
            <>
              <div className="h-px bg-gray-100" />
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="text-center">
                  <div className="font-semibold text-emerald-600">
                    {dataPoint.count - (dataPoint.warningCount + dataPoint.criticalCount)}
                  </div>
                  <div className="text-gray-400">Normal</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-amber-600">{dataPoint.warningCount}</div>
                  <div className="text-gray-400">Warning</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-red-600">{dataPoint.criticalCount}</div>
                  <div className="text-gray-400">Critical</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div>
          <h3 className="font-semibold text-gray-900">
            {data?.kpi?.name ?? 'KPI'} Trend
          </h3>
          <p className="text-sm text-gray-500">Value over time</p>
        </div>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : chartData.length === 0 ? (
          <EmptyChartState message="No measurements yet for this period" />
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={formatYAxis}
                  domain={[0, 'auto']}
                />
                <Tooltip content={<CustomTooltip />} />

                {/* Threshold reference lines */}
                {thresholds?.warning != null && (
                  <ReferenceLine
                    y={thresholds.warning}
                    stroke="#f59e0b"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                    label={{
                      value: 'Warning',
                      position: 'right',
                      fill: '#f59e0b',
                      fontSize: 10,
                      fontWeight: 500,
                    }}
                  />
                )}
                {thresholds?.critical != null && (
                  <ReferenceLine
                    y={thresholds.critical}
                    stroke="#ef4444"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                    label={{
                      value: 'Critical',
                      position: 'right',
                      fill: '#ef4444',
                      fontSize: 10,
                      fontWeight: 500,
                    }}
                  />
                )}

                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#00674a"
                  strokeWidth={2.5}
                  dot={{ fill: '#00674a', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, strokeWidth: 2, fill: '#fff', stroke: '#00674a' }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// GROUP-BY CHART
// ============================================

function GroupByChart({
  data,
  isLoading,
  groupBy,
  options,
  onGroupByChange,
  unit,
  displayFormat,
}: {
  data: KpiAnalyticsResponse | undefined;
  isLoading: boolean;
  groupBy: GroupBy;
  /** The groupings offered; G-19 leaves out the operator ones (Phase 41A). */
  options: typeof GROUP_BY_OPTIONS;
  onGroupByChange: (gb: GroupBy) => void;
  unit: string | null;
  displayFormat: string | null;
}) {
  // Phase 41A: day and week bars keep the API's date order and show their
  // org-local date; every other grouping is sorted by value
  const barChartData = useMemo(() => {
    const dateStyle = data?.groupBy === 'day' || data?.groupBy === 'week' ? data.groupBy : null;
    const bars =
      data?.groups?.map((group) => {
        let status: 'normal' | 'warning' | 'critical' = 'normal';
        if (group.criticalCount > 0) status = 'critical';
        else if (group.warningCount > 0) status = 'warning';

        return {
          name: (dateStyle ? formatDayKey(group.key, dateStyle) : group.key) || 'Unknown',
          value: group.value ?? 0,
          count: group.count,
          status,
        };
      }) ?? [];
    return dateStyle ? bars : bars.sort((a, b) => b.value - a.value);
  }, [data]);

  // Phase 41A: the heading already says "By Week", so the axis drops the
  // "Week of " that the tooltip keeps
  const formatCategoryTick = (name: string) =>
    data?.groupBy === 'week' ? name.replace(/^Week of /, '') : name;

  const CustomBarTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{
      payload: { name: string; value: number; count: number; status: string };
    }>;
  }) => {
    if (!active || !payload?.length) return null;

    const d = payload[0].payload;
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
        <p className="font-medium text-gray-900">{d.name}</p>
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-gray-500">Value</span>
            <span className="font-bold text-gray-900 tabular-nums">
              {formatKpiValue(d.value, unit, displayFormat)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-gray-500">Measurements</span>
            <span className="font-medium text-gray-700">{d.count}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div>
          <h3 className="font-semibold text-gray-900">
            By {GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label}
          </h3>
          <p className="text-sm text-gray-500">Comparison across groups</p>
        </div>

        {/* Group By Toggle */}
        <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => onGroupByChange(option.value)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200',
                groupBy === option.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <option.icon size={12} />
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : barChartData.length === 0 ? (
          <EmptyChartState message="No data available for this grouping" />
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barChartData} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickFormatter={(v: number) => formatKpiValue(v, unit, displayFormat)}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickFormatter={formatCategoryTick}
                  width={75}
                />
                <Tooltip content={<CustomBarTooltip />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {barChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// MEASUREMENTS TABLE
// ============================================

function MeasurementsTable({
  data,
  isError,
  isStale,
  page,
  lastPage,
  statusChip,
  chipCounts,
  onStatusChange,
  onPageChange,
  headingRef,
  unit,
  displayFormat,
  timeZone,
  aggregationType,
  cumulativeHref,
  navigate,
  detailsFrom,
}: {
  data: KpiMeasurementsResponse | undefined;
  isError: boolean;
  /** True while the previous page's rows stand in for the page being fetched. */
  isStale: boolean;
  /** The URL's page (1-based); it can run ahead of the rows during a page turn. */
  page: number;
  /** The list's last page; null until its total is known. */
  lastPage: number | null;
  statusChip: StatusChipId | null;
  chipCounts: StatusChipCounts;
  onStatusChange: (chip: StatusChipId | null) => void;
  onPageChange: (page: number) => void;
  /** Phase 41A: the "N flagged" card moves focus here. */
  headingRef: RefObject<HTMLHeadingElement | null>;
  unit: string | null;
  displayFormat: string | null;
  /** Phase 41A: the org's IANA zone; undefined reads in the browser's zone. */
  timeZone: string | undefined;
  /**
   * Phase 30 Wave 5 §7.1: when `'sum'` or `'count'`, per-row threshold badges
   * are suppressed (every row would otherwise show "critical" for a 1-event-
   * per-trigger metric whose target is per-shift-window). A neutral pill links
   * to the cumulative view instead.
   */
  aggregationType: KpiAggregation | null;
  /** Phase 30 Wave 5 §7.1: the cumulative-view deep link (current params merged). */
  cumulativeHref: string;
  navigate: ReturnType<typeof useNavigate>;
  /** Phase 41A: this page's URL, passed to the trigger page for its back link. */
  detailsFrom: string;
}) {
  const measurements = data?.measurements ?? [];
  // Phase 30 Wave 5 §7.1: badges are misleading for accumulating KPIs.
  const suppressBadges = aggregationType === 'sum' || aggregationType === 'count';

  // Phase 41A: the rows whose Why panel is open. A page change resets the set
  // (state adjusted during render, like the as-of snapshot in KpiAnalyticsPage).
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(NO_EXPANDED_ROWS);
  const [expandedPage, setExpandedPage] = useState(page);
  if (expandedPage !== page) {
    setExpandedPage(page);
    setExpandedIds(NO_EXPANDED_ROWS);
  }
  const toggleWhy = (rowId: string) => {
    setExpandedIds((current) => {
      const ids = new Set(current);
      if (!ids.delete(rowId)) ids.add(rowId);
      return ids;
    });
  };

  const total = data?.pagination.total ?? 0;
  const activeChip = STATUS_CHIPS.find((chip) => chip.id === statusChip) ?? null;
  // A page past the end shows the spinner until the clamp lands on the last page
  const clamping = lastPage !== null && page > lastPage;
  const showRows = !isError && !clamping && measurements.length > 0;
  const showEmpty = !isError && !clamping && data !== undefined && total === 0;
  // The footer describes the rows on screen, which during a page turn are still
  // the previous page's (the response's own offset, not the URL's page)
  const shownOffset = data?.pagination.offset ?? 0;
  const footerText =
    showRows && lastPage !== null
      ? `Showing ${shownOffset + 1}–${shownOffset + measurements.length} of ${total} · Page ${
          Math.floor(shownOffset / PAGE_SIZE) + 1
        } of ${lastPage}`
      : '';

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-6 py-4">
        <h3
          ref={headingRef}
          tabIndex={-1}
          className="scroll-mt-32 font-semibold text-gray-900"
        >
          Results
        </h3>
        <p className="text-sm text-gray-500">
          Saved results in this period · Newest analysis first
        </p>
        {/* Phase 41A: chips filter the table only; counts come from the summary */}
        <div role="group" aria-label="Filter results by status" className="mt-3 flex flex-wrap gap-2">
          <StatusChip
            label="All"
            count={chipCounts.all}
            pressed={statusChip === null}
            onClick={() => onStatusChange(null)}
          />
          {STATUS_CHIPS.map((chip) => (
            <StatusChip
              key={chip.id}
              label={chip.label}
              count={chipCounts[chip.id]}
              pressed={statusChip === chip.id}
              onClick={() => onStatusChange(chip.id)}
            />
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        {isError ? (
          <div className="flex h-48 items-center justify-center text-gray-500">
            Results could not be loaded.
          </div>
        ) : showEmpty ? (
          <div className="flex h-48 items-center justify-center text-gray-500">
            {activeChip
              ? `No ${activeChip.emptyName} results in this period.`
              : 'No results in this period.'}
          </div>
        ) : !showRows ? (
          <div className="flex h-48 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <table
            className={cn('w-full transition-opacity', isStale && 'opacity-60')}
            aria-busy={isStale || undefined}
          >
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Filmed
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  KPI
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Value
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Confidence
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {measurements.map((measurement) => {
                // Phase 41A: rows with a Why view (S16: the previous API sends none)
                const why = hasWhyPanel(measurement.why) ? measurement.why : null;
                const whyOpen = why !== null && expandedIds.has(measurement.id);
                const panelId = `kpi-why-row-${measurement.id}`;
                // A row with no value shows its grey pill alone: a step count beside
                // "Can't assess" reads as a contradiction. The Why panel keeps it.
                const headline =
                  measurement.primaryValue === null
                    ? null
                    : whyHeadline(why, measurement.thresholdStatus);
                return (
                  <Fragment key={measurement.id}>
                    <tr className={cn('hover:bg-gray-50', whyOpen && 'bg-gray-50/60')}>
                      {/* Phase 41A: the clip window wraps (balanced, so it breaks
                          near the dash) to keep the table inside its card; the
                          min width keeps it legible where the table must scroll */}
                      <td className="min-w-44 text-balance px-4 py-4 text-sm text-gray-900">
                        <FilmedCell measurement={measurement} timeZone={timeZone} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                        {measurement.definition?.name ?? '--'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        <span className="text-lg font-bold text-gray-900 tabular-nums">
                          {measurement.primaryValue === null
                            ? '—'
                            : formatKpiValue(
                                measurement.primaryValue,
                                measurement.definition?.unit ?? unit,
                                displayFormat
                              )}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        {suppressBadges && measurement.primaryValue !== null ? (
                          // Phase 30 Wave 5 §7.1: sum/count KPIs roll up across a shift
                          // window; per-row colored badges are misleading. Render a
                          // neutral pill that links to the cumulative view.
                          <Link
                            to={cumulativeHref}
                            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200"
                            title="Per-row threshold doesn't apply — this KPI accumulates across the shift window."
                          >
                            Part of shift window
                            <ExternalLink size={10} />
                          </Link>
                        ) : (
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
                              getThresholdStatusBgColor(measurement.thresholdStatus),
                              getThresholdStatusColor(measurement.thresholdStatus)
                            )}
                          >
                            <span
                              className={cn(
                                'h-1.5 w-1.5 rounded-full',
                                getThresholdDotColor(measurement.thresholdStatus)
                              )}
                            />
                            {/* Phase 41A (D3): a saved result with no value has no
                                status (grey); its label comes from the stored
                                observation status, whatever the KPI's aggregation */}
                            {measurement.primaryValue === null
                              ? noValueLabel(rowObservationStatus(measurement))
                              : (measurement.thresholdStatus ?? 'Unknown')}
                          </span>
                        )}
                        {/* Phase 41A (G-21): what the status rests on. It wraps
                            anywhere, so it never widens the table. */}
                        {headline && (
                          <p className="mt-1 max-w-48 whitespace-normal text-xs text-gray-500 [overflow-wrap:anywhere]">
                            {headline}
                          </p>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                        {measurement.confidence !== null
                          ? `${(measurement.confidence * 100).toFixed(0)}%`
                          : '--'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-right">
                        {/* Phase 41A: the two actions stack, so the expander adds
                            no column and no width (the table fills its card at 1280 px) */}
                        <div className="flex flex-col items-end gap-1.5">
                          {measurement.triggerId && (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/assessments/triggers/${measurement.triggerId}`, {
                                  state: { from: detailsFrom },
                                })
                              }
                              className="inline-flex items-center gap-1 text-sm text-guild-green hover:text-guild-green/80"
                            >
                              <ExternalLink size={14} />
                              View details
                            </button>
                          )}
                          {why !== null && (
                            <button
                              type="button"
                              aria-expanded={whyOpen}
                              aria-controls={panelId}
                              onClick={() => toggleWhy(measurement.id)}
                              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                            >
                              <ChevronDown
                                size={14}
                                className={cn('transition-transform', whyOpen && 'rotate-180')}
                              />
                              {whyOpen ? 'Hide why' : 'Show why'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Phase 41A: the row's Why panel. Always rendered (hidden while
                        closed) so the expander's aria-controls always resolves. */}
                    {why !== null && (
                      <tr hidden={!whyOpen} className="!border-t-0 bg-gray-50/60">
                        <td colSpan={6} className="px-4 pb-4">
                          <div
                            id={panelId}
                            className="rounded-lg border border-gray-100 bg-white p-4"
                          >
                            <KpiWhyPanel
                              why={why}
                              observationStatus={rowObservationStatus(measurement)}
                              thresholdStatus={measurement.thresholdStatus}
                              clipStart={measurement.footageStart ?? null}
                              timeZone={timeZone}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Phase 41A: the live region stays mounted so each new page range is announced */}
      <div
        className={cn(
          'flex items-center justify-between gap-4 px-6',
          showRows && 'border-t border-gray-100 py-3'
        )}
      >
        <p className="text-sm text-gray-500" aria-live="polite">
          {footerText}
        </p>
        {showRows && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={lastPage === null || page >= lastPage}
              className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Phase 41A: one status filter chip. The count is part of its accessible name. */
function StatusChip({
  label,
  count,
  pressed,
  onClick,
}: {
  label: string;
  count: number;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={`${label}, ${countResults(count)}`}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        pressed
          ? 'border-guild-green bg-guild-green text-white'
          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
      )}
    >
      {label}
      <span
        className={cn(
          'rounded-full px-1.5 tabular-nums',
          pressed ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
        )}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * Phase 41A (G-18, D5): when the clip was filmed, in the org's zone, with the
 * analysis time under it. A result with no trigger (or from the previous API,
 * which sends no footage window) shows its analysis time, labelled as such.
 */
function FilmedCell({
  measurement,
  timeZone,
}: {
  measurement: KpiMeasurementListItem;
  timeZone: string | undefined;
}) {
  const filmed = formatFootageWindow(measurement.footageStart, measurement.footageEnd, timeZone);
  if (!filmed) {
    return (
      <>
        {formatInTimeZone(measurement.measuredAt, timeZone, {
          ...DATE_TIME_OPTIONS,
          timeZoneName: 'short',
        })}{' '}
        <span className="text-gray-500">(analysis time)</span>
      </>
    );
  }
  return (
    <>
      <div>{filmed}</div>
      <div className="text-xs text-gray-500">
        analyzed {formatInTimeZone(measurement.measuredAt, timeZone, DATE_TIME_OPTIONS)}
      </div>
    </>
  );
}

// ============================================
// SHARED COMPONENTS
// ============================================

function EmptyChartState({ message }: { message: string }) {
  return (
    <div className="flex h-64 flex-col items-center justify-center text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
        <BarChart3 className="h-7 w-7 text-gray-400" />
      </div>
      <p className="mt-3 text-sm font-medium text-gray-900">No data available</p>
      <p className="mt-1 text-sm text-gray-500">{message}</p>
    </div>
  );
}

export default KpiAnalyticsPage;
